import React, { useEffect, useRef, useState } from 'react';
import HotmailDeliveryNotice from './HotmailDeliveryNotice';
import './ChatbotWidget.css';

const ICONS = {
  chat: 'M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-7l-5 4v-4H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  help: 'M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20zm-1-6h2v2h-2v-2zm1-12a4 4 0 0 1 4 4c0 2-3 2.5-3 5h-2c0-3 3-3 3-5a2 2 0 1 0-4 0H8a4 4 0 0 1 4-4z',
  sparkle: 'M12 2l2.5 6.5L21 11l-6.5 2.5L12 20l-2.5-6.5L3 11l6.5-2.5L12 2z',
  pokeball: 'M12 2a10 10 0 0 0-9.95 9h7.05a3 3 0 0 1 5.8 0h7.05A10 10 0 0 0 12 2zm9.95 11h-7.05a3 3 0 0 1-5.8 0H2.05A10 10 0 0 0 12 22a10 10 0 0 0 9.95-9z',
};

const STOPWORDS = new Set(['the','and','for','you','your','our','what','when','where','how','why','who','can','does','do','is','are','will','have','has','was','were','any','all','some','about','from','this','that','with','into','out']);
const SYNONYMS = {
  ship: ['shipping','shipped','delivery','deliver','postage','mail','post'],
  shipping: ['ship','delivery','deliver','postage'],
  return: ['returns','refund','refunds','exchange'],
  buy: ['buylist','buying','sell','selling','trade','tradein'],
  pay: ['payment','pricing','price','cost','fee','rates','rate'],
  cards: ['card','single','singles','sealed','pack','packs'],
  track: ['tracking','where','status','order'],
};
function tokenize(s) {
  const base = String(s || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter(w => w.length > 2 && !STOPWORDS.has(w));
  const expanded = new Set(base);
  for (const w of base) {
    for (const [root, alts] of Object.entries(SYNONYMS)) {
      if (w === root || alts.includes(w)) {
        expanded.add(root);
        for (const a of alts) expanded.add(a);
      }
    }
  }
  return [...expanded];
}
function bestMatches(query, faqs, max = 3) {
  const qTokens = tokenize(query);
  if (!qTokens.length) return [];
  return faqs
    .map(f => {
      const tTokens = new Set(tokenize(f.question + ' ' + f.answer + ' ' + (f.category || '')));
      const score = qTokens.reduce((n, w) => n + (tTokens.has(w) ? 1 : 0), 0);
      return { faq: f, score };
    })
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, max)
    .map(x => x.faq);
}

export default function ChatbotWidget() {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [lead, setLead] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const scrollRef = useRef(null);
  const lastMatchedQuestion = useRef(null);

  useEffect(() => {
    fetch('/api/chatbot/config')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.settings?.enabled) return;
        setConfig(data);
        setMessages([{ from: 'bot', text: data.settings.greeting || 'Hi! How can we help?' }]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  if (!config) return null;
  const { settings, faqs } = config;
  const color = settings.color || '#0d5c3a';

  function pushBot(text, extras = {}) {
    setMessages(m => [...m, { from: 'bot', text, ...extras }]);
  }
  function pushUser(text) {
    setMessages(m => [...m, { from: 'user', text }]);
  }

  function answer(faq) {
    lastMatchedQuestion.current = faq.question;
    pushUser(faq.question);
    setTimeout(() => pushBot(faq.answer, { suggestions: faqs.slice(0, 4) }), 250);
  }

  async function handleSend(e) {
    e?.preventDefault?.();
    const q = input.trim();
    if (!q) return;
    pushUser(q);
    setInput('');

    // Local FAQ match first (no roundtrip).
    const matches = bestMatches(q, faqs);
    if (matches.length) {
      lastMatchedQuestion.current = matches[0].question;
      setTimeout(() => pushBot(matches[0].answer, { suggestions: matches.slice(1) }), 250);
      return;
    }

    // Server-side ask — server may try FAQ again (in case widget is stale)
    // and fall back to LLM if enabled.
    pushBot('…', { typing: true });
    try {
      const r = await fetch('/api/chatbot/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q }),
      });
      const data = r.ok ? await r.json() : null;
      setMessages(m => m.filter(x => !x.typing));
      if (data?.answer) {
        lastMatchedQuestion.current = q;
        pushBot(data.answer, {
          suggestions: faqs.slice(0, 3),
          source: data.source,
        });
      } else {
        lastMatchedQuestion.current = q;
        pushBot(
          "I couldn't find an answer for that. Try one of the common questions below, or leave us a message:",
          { offerLead: true, suggestions: faqs.slice(0, 4) }
        );
      }
    } catch (_) {
      setMessages(m => m.filter(x => !x.typing));
      pushBot(
        "Sorry — connection issue. Try a question below or leave us a message:",
        { offerLead: true, suggestions: faqs.slice(0, 4) }
      );
    }
  }

  async function submitLead(e) {
    e.preventDefault();
    if (!lead.email && !lead.phone) return;
    try {
      await fetch('/api/chatbot/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...lead,
          matched_question: lastMatchedQuestion.current || null,
          page_url: window.location.href,
        }),
      });
      setSubmitted(true);
      setShowLeadForm(false);
      pushBot(settings.thanks_message || 'Thanks! We will get back to you shortly.');
    } catch (_) {
      pushBot('Sorry — something went wrong sending that. Please try again or email support@silvergames.ca.');
    }
  }

  const iconPath = ICONS[settings.icon] || ICONS.chat;

  return (
    <>
      <button
        className="chatbot-fab"
        style={{ background: color }}
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {settings.icon === 'custom' && settings.icon_url ? (
          <img src={settings.icon_url} alt="" />
        ) : (
          <svg viewBox="0 0 24 24" width="26" height="26" fill="#fff" aria-hidden="true">
            <path d={iconPath} />
          </svg>
        )}
      </button>

      {open && (
        <div className="chatbot-panel" role="dialog" aria-label="Chat">
          <header className="chatbot-panel__header" style={{ background: color }}>
            <span>{settings.title || 'How can we help?'}</span>
            <button onClick={() => setOpen(false)} aria-label="Close">×</button>
          </header>

          <div className="chatbot-panel__body" ref={scrollRef}>
            {messages.length === 1 && faqs.length > 0 && (
              <div className="chatbot-quick">
                <div className="chatbot-quick__label">Pick a question:</div>
                {faqs.slice(0, 6).map(f => (
                  <button key={f.id} className="chatbot-quick__btn" onClick={() => answer(f)}>
                    {f.question}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`chatbot-msg chatbot-msg--${m.from}`}>
                <div
                  className="chatbot-msg__bubble"
                  style={m.from === 'user' ? { background: color, color: '#fff' } : null}
                >
                  {m.typing ? <em style={{ color: '#888' }}>typing…</em> : m.text}
                  {m.source === 'llm' && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>
                      AI-generated · double-check anything important
                    </div>
                  )}
                </div>
                {m.suggestions && m.suggestions.length > 0 && (
                  <div className="chatbot-suggestions">
                    {m.suggestions.map(f => (
                      <button key={f.id} onClick={() => answer(f)}>{f.question}</button>
                    ))}
                  </div>
                )}
                {m.offerLead && !showLeadForm && !submitted && (
                  <div className="chatbot-suggestions">
                    <button
                      onClick={() => setShowLeadForm(true)}
                      style={{ background: color, color: '#fff', borderColor: color, padding: '8px 14px', fontWeight: 600 }}
                    >
                      {settings.lead_capture_label || 'Send us a message'}
                    </button>
                  </div>
                )}
              </div>
            ))}

            {showLeadForm && !submitted && (
              <form className="chatbot-lead-form" onSubmit={submitLead}>
                <input placeholder="Your name" value={lead.name} onChange={e => setLead({ ...lead, name: e.target.value })} />
                <input type="email" placeholder="Email *" value={lead.email} onChange={e => setLead({ ...lead, email: e.target.value })} required />
                <HotmailDeliveryNotice email={lead.email} />
                <input type="tel" placeholder="Phone (optional)" value={lead.phone} onChange={e => setLead({ ...lead, phone: e.target.value })} />
                <textarea placeholder="How can we help?" rows={3} value={lead.message} onChange={e => setLead({ ...lead, message: e.target.value })} />
                <button type="submit" style={{ background: color }}>Send</button>
              </form>
            )}
          </div>

          <form className="chatbot-panel__input" onSubmit={handleSend}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Type a question…"
              aria-label="Chat input"
            />
            <button type="submit" style={{ background: color }} aria-label="Send">→</button>
          </form>
        </div>
      )}
    </>
  );
}
